import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { submitChat } from "../lib/api";
import { toast } from "sonner";

export const FloatingChat = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [text, setText] = useState("");
  const [messages, setMessages] = useState([
    {
      from: "agent",
      text: "Hi! I'm from DVP DIGI WORLD. How can I help you today — personal loan, business loan, insurance or something else?",
    },
  ]);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setSending(true);
    setMessages((m) => [...m, { from: "user", text: value }]);
    setText("");
    try {
      await submitChat({ name, contact, message: value });
      setTimeout(() => {
        setMessages((m) => [
          ...m,
          {
            from: "agent",
            text: "Thanks! Our team has received your message and will reach out on the contact you've shared within business hours.",
          },
        ]);
      }, 600);
    } catch (err) {
      toast.error("Couldn't send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle-button"
        className={`fixed bottom-6 left-6 z-50 h-14 w-14 rounded-full shadow-2xl transition-all flex items-center justify-center text-white ${
          open
            ? "bg-[#1d4ed8] hover:bg-[#2563eb]"
            : "bg-[#dc2626] hover:bg-[#ef4444]"
        }`}
        aria-label={open ? "Close chat" : "Open chat"}
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 left-4 right-4 sm:left-6 sm:right-auto z-50 sm:w-96 max-h-[75vh] rounded-xl overflow-hidden border border-white/10 bg-[#121212] shadow-2xl flex flex-col"
        >
          <div className="bg-gradient-to-r from-[#1d4ed8] to-[#dc2626] p-4">
            <div className="text-white font-display text-lg font-bold">
              Chat with DVP DIGI WORLD
            </div>
            <div className="text-white/80 text-xs font-mono uppercase tracking-[0.2em] mt-1">
              Typically replies in minutes
            </div>
          </div>

          <div className="p-3 border-b border-white/10 grid grid-cols-2 gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              data-testid="chat-input-name"
              className="bg-[#1a1a1a] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-neutral-500 focus:border-[#dc2626] outline-none"
            />
            <input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Phone or email"
              data-testid="chat-input-contact"
              className="bg-[#1a1a1a] border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-neutral-500 focus:border-[#dc2626] outline-none"
            />
          </div>

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[200px] max-h-[300px]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.from === "user"
                      ? "bg-[#1d4ed8] text-white rounded-br-sm"
                      : "bg-[#1a1a1a] text-neutral-200 rounded-bl-sm border border-white/5"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
          </div>

          <form
            onSubmit={send}
            className="p-3 border-t border-white/10 flex items-center gap-2 bg-[#0f0f0f]"
          >
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type a message…"
              data-testid="chat-input-message"
              className="flex-1 bg-[#1a1a1a] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-neutral-500 focus:border-[#dc2626] outline-none"
            />
            <button
              type="submit"
              disabled={sending || !text.trim()}
              data-testid="chat-send-button"
              className="h-10 w-10 flex items-center justify-center bg-[#dc2626] hover:bg-[#ef4444] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default FloatingChat;
